import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, StateManager, useBCMSContext, useBCMSModal, useModel, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import { CustomModalContent, CustomModalFooter } from "components/custom-ui/CustomModal";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { FormContainer, FormRowView } from "components/shared";
import { PrimaryButton, SecondaryButton, UINumberInput } from "components/ui";
import { UITextClearInput } from "components/ui/UITextInput";
import AddUnlinkedTransferAccountForm from "components/workflows/AddUnlinkedTransferAccountWorkflow/classes/AddUnlinkedTransferAccountForm";
import AddUnlinkedTransferAccountFormUI from "components/workflows/AddUnlinkedTransferAccountWorkflow/classes/AddUnlinkedTransferAccountFormUI";
import Classes, { Account, UnlinkedAccount } from "model";
import { SVGAsset } from "res";
import "./styles.css";

export default function AddUnlinkedTransferToAnotherMembersAccountView(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  const { branding } = useModel();
  const data = { ...getDefaultData(branding), ...props.data ?? {} };
  const ui = { ...getDefaultUI(branding), ...props.ui ?? {} };
  const { onAddAccountModalButtonClick } = useStateManager();

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      // Add an unlinked account globally and then execute a callback from the state manager hook.
      modalOnAddAccountClick: () => {
        const form = state.data.form;
        const nextID = Account.nextID();
        const defaultAcc = BCMSDefaultModel.account();
        const defaultAccPersonal=  BCMSDefaultModel.unlinkedAccount();
        const defaultUnlinkedAccBusiness = BCMSDefaultModel.unlinkedAccountBusiness();
        const isAcctPersonalType = form.accountOwnerType.toLowerCase() === "personal" || form.accountOwnerType === "";
        const defaultUnlinkedAcc = isAcctPersonalType ? defaultAccPersonal : defaultUnlinkedAccBusiness;

        defaultUnlinkedAcc.name = `${ defaultUnlinkedAcc.accountOwnerFirstName } ${ defaultUnlinkedAcc.accountOwnerLastName }`;
        defaultUnlinkedAcc.nickname = isAcctPersonalType? defaultUnlinkedAcc.accountOwnerFirstName : defaultUnlinkedAcc.nickname;

        // START: Branding options will clear certain fields in the data and tables.
        const control = state.ui.control;
        
        if(!control.accountOwnerType) {
          defaultUnlinkedAcc.accountOwnerType = "";
        }
        if(!control.firstNameOfAccountOwner) {
          defaultUnlinkedAcc.accountOwnerFirstName = "";
        }
        if(!control.lastNameOfAccountOwner) {
          defaultUnlinkedAcc.accountOwnerLastName = "";
        }
        if(isAcctPersonalType && !control.firstNameOfAccountOwner && !control.lastNameOfAccountOwner) {
          defaultUnlinkedAcc.accountOwner = "";
        }
        if(isAcctPersonalType && control.firstNameOfAccountOwner && !control.lastNameOfAccountOwner) {
          defaultUnlinkedAcc.accountOwner = defaultUnlinkedAcc.accountOwnerFirstName;
        }
        if(isAcctPersonalType && !control.firstNameOfAccountOwner && control.lastNameOfAccountOwner) {
          defaultUnlinkedAcc.accountOwner = defaultUnlinkedAcc.accountOwnerLastName;
        }
        // END: Branding options will clear certain fields in the data and tables.

        let defaultUnlinkedAccRecord = defaultUnlinkedAcc.record;

        const busUnlinkedAcc = {
              number: form.accountNumber ? Account.formatAccountNumber(form.accountNumber) : defaultUnlinkedAcc.number,
              accountOwner: form.accountOwner,
         
        }


        const newUnlinkedAcc = UnlinkedAccount.init({
          nickname: form.accountNickname,
          // Set the account owner name conditionally.
          accountOwner: form.lastNameOfAccountOwner === ""
          ? form.firstNameOfAccountOwner
          : `${ form.firstNameOfAccountOwner} ${ form.lastNameOfAccountOwner }`,
          accountOwnerType: form.accountOwnerType,
          accountOwnerFirstName: form.firstNameOfAccountOwner,
          accountOwnerLastName: form.lastNameOfAccountOwner,
          subtype: form.accountType ? form.accountType.toLowerCase() : "checking",
          number: form.accountNumber ? Account.formatAccountNumber(form.accountNumber) : Account.getNextMaskedAccountNumber(),
          // Set the account name conditionally.
          name: form.lastNameOfAccountOwner === ""
          ? form.firstNameOfAccountOwner
          : `${ form.firstNameOfAccountOwner} ${ form.lastNameOfAccountOwner }`,
          ...(isAcctPersonalType ? {} : busUnlinkedAcc)
        });

        
        const newUnlinkedAccRecord = newUnlinkedAcc.record;
        newUnlinkedAccRecord.id = nextID;
        newUnlinkedAccRecord.mapID = nextID;
        const mergeUnlinkedAccRecords = BCMSDefaultModel.mergeRecords(newUnlinkedAccRecord, defaultUnlinkedAccRecord);

        defaultAcc.id = nextID;
        defaultAcc.type = "unlinked";
        defaultAcc.name = mergeUnlinkedAccRecords.name;
        defaultAcc.nickname = mergeUnlinkedAccRecords.nickname;
        defaultAcc.number = mergeUnlinkedAccRecords.number;

        const newRecords = [
          Account.create(defaultAcc.record),
          UnlinkedAccount.create(mergeUnlinkedAccRecords)
        ];

        BCMSRecord.batch(newRecords, context, Classes);

        const addedUnlinkedAcc = UnlinkedAccount.initByID(nextID);
        onAddAccountModalButtonClick(addedUnlinkedAcc);
      },

      onChange: prop => value => {
        const crud = BCMSStateManagerCRUD.init(state)

        if (prop === "accountOwnerType" && value !== "personal") {
          crud
            .update(["data", "form", "firstNameOfAccountOwner"], "")
            .update(["data", "form", "lastNameOfAccountOwner"], "")
        } else if(prop === "accountOwnerType" && value !== "business"){
          crud.update(["data", "form", "accountOwner"], "")
        }
        
        crud
        .update([ "data", "form", prop ], value)
        .submit(setState);
      }
  });

  return (
    <StateManager state={ { data, ui } } controller={ controller }>
      <Content/>
    </StateManager>
  );
}

export const getDefaultData = branding => {
  const options = branding ?? {};
  const form = AddUnlinkedTransferAccountForm.init({}, options);
  return {
    form
  }
}

export const getDefaultUI = branding => {
  const options = branding ?? {};
  const form = AddUnlinkedTransferAccountForm.init({}, options);

  return {
    control: AddUnlinkedTransferAccountFormUI.init(form, options)
  }
}


function Content(props) {
  const { prevStep } = useWorkflow() ?? {};
  const { data, ui, onChange, modalOnAddAccountClick } = useStateManager();
  const { close } = useBCMSModal();
  const { branding, settings } = useModel();
  const { options } = branding;
  const form = data.form;
  const control = ui.control;
  const hasTransferAccountTypeModal = options.transfer_account_type_modal_abi22 === "true";

  const onAddAccountClick = () => {
    modalOnAddAccountClick();
    close("AddUnlinkedTransferAccountModal");
  };

  const onBackClick = () => {
    if(hasTransferAccountTypeModal) {
      prevStep();
    } else {
      close("AddUnlinkedTransferAccountModal");
    }
  }

  return (
    <>
      <CustomModalContent className="flex-container flex-horizontal align-top">
        <div className="add-unlinked-transfer-to-another-members-account-view">
          <div className="flex-container flex-vertical header-container">
            <b>{ `Transfer to Another Member's Account at ${ settings.fiName }` }</b>
        
              <FormContainer className="small-form-width">
                { control.accountNickname && 
                  <FormRowView text={
                    <div className="flex-container flex-horizontal align-center">
                        <p className="label-with-icon">Account Nickname</p>
                        <SVGAsset id="InfoIcon" />
                    </div>
                  }>
                      <UITextClearInput value={ form.accountNickname } dataPoint="Add_Unlinked_Transfer_Account_Modal_Account_Nickname_Input" onChange={ onChange("accountNickname") } />
                  </FormRowView> 
                }
                { control.accountOwnerType && 
                  <FormRowView text="Account Owner Type">
                      <CustomSelect value={ form.accountOwnerType.toLowerCase() } onChange={ onChange("accountOwnerType") }>
                        <CustomSelectItem value={ 'personal' }> { `Personal` }</CustomSelectItem>
                        <CustomSelectItem value={ 'business' }> { `Business` }</CustomSelectItem>
                      </CustomSelect>
                  </FormRowView> 
                }


              {(form.accountOwnerType.toLowerCase() === "personal" ||form.accountOwnerType === "" ) && <>
                {control.firstNameOfAccountOwner &&
                  <FormRowView text="First Name of Account Owner">
                    <UITextClearInput value={form.firstNameOfAccountOwner} onChange={onChange("firstNameOfAccountOwner")} />
                  </FormRowView>
                }
                {control.lastNameOfAccountOwner &&
                  <FormRowView text="Last Name of Account Owner">
                    <UITextClearInput value={form.lastNameOfAccountOwner} onChange={onChange("lastNameOfAccountOwner")} />
                  </FormRowView>
                }
              </>
              }

              {(form.accountOwnerType && form.accountOwnerType.toLowerCase() === "business") &&
                <FormRowView text="Business Name of Account Owner">
                  <UITextClearInput value={form.accountOwner} onChange={onChange("accountOwner")} />
                </FormRowView>
              }
                { control.accountType && 
                  <FormRowView text="Account Type">
                      <CustomSelect value={ form.accountType.toLowerCase() } onChange={ onChange("accountType") }>
                        <CustomSelectItem value={ 'checking' }> { `Checking` }</CustomSelectItem>
                        <CustomSelectItem value={ 'savings' }> { `Savings` }</CustomSelectItem>
                      </CustomSelect>
                  </FormRowView> 
                }
                { control.accountNumber && 
                  <FormRowView text={
                    <div className="flex-container flex-horizontal align-center">
                        <p className="label-with-icon">Account Number</p>
                        <SVGAsset id="InfoIcon" />
                    </div>
                  }>
                      <UINumberInput value={ form.accountNumber } onChange={ onChange("accountNumber") } />
                  </FormRowView> 
                }
                { control.confirmAccountNumber && 
                  <FormRowView text="Confirm Account Number">
                      <UINumberInput value={ form.confirmAccountNumber } onChange={ onChange("confirmAccountNumber") } />
                  </FormRowView> 
                }
              </FormContainer>
          </div>
        </div>
      </CustomModalContent>
      <CustomModalFooter className="form-button-container button-container flex-container flex-horizontal align-center">
        <SecondaryButton text="Back" onClick={ onBackClick } />
        <PrimaryButton text="Add Account" dataPoint="Add_Unlinked_Transfer_Account_Modal_Add_Account_Button" onClick={ onAddAccountClick }/>
      </CustomModalFooter>
    </>
  );
}
